import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import icon from "../../images/icons/icon_search.svg";
import icon1 from "../../images/icons/icon_calendar.svg";

/* Utils */
const formatDMY = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const toTagString = (t) => {
  if (t == null) return "";
  return String(t).trim();
};

/* Build a popularity-sorted unique tag list from blogs (defensive) */
const getPopularTags = (blogs, limit = 10) => {
  const counts = new Map();

  blogs.forEach((b) => {
    let raw = [];

    if (Array.isArray(b?.tags)) {
      raw = b.tags;
    } else if (typeof b?.tags === "string") {
      raw = b.tags.split(",");
    }

    raw
      .map(toTagString)
      .filter(Boolean)
      .forEach((t) => counts.set(t, (counts.get(t) || 0) + 1));
  });

  const entries = Array.from(counts.entries());

  entries.sort((x, y) => {
    const [tagA, countA] = x;
    const [tagB, countB] = y;

    // Primary: by frequency (desc)
    if (countA !== countB) return countB - countA;

    // Secondary: alpha by tag (case-insensitive)
    const aStr = toTagString(tagA).toLowerCase();
    const bStr = toTagString(tagB).toLowerCase();

    // Use localeCompare safely
    if (typeof aStr.localeCompare === "function") {
      return aStr.localeCompare(bStr);
    }
    // Fallback (shouldn't happen)
    return aStr > bStr ? 1 : aStr < bStr ? -1 : 0;
  });

  return entries.slice(0, limit).map(([tag]) => tag);
};

const BlogSidebar = () => {
  const [blogs, setBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(false);
  const [errorBlogs, setErrorBlogs] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoadingBlogs(true);
      setErrorBlogs("");
      try {
        const response = await axios.get("https://api.rashtechinfo.com/getAllBlog");
        const data = Array.isArray(response.data) ? response.data : [];
        setBlogs(data);
      } catch (err) {
        console.error(err);
        setErrorBlogs("Failed to load blogs.");
      } finally {
        setLoadingBlogs(false);
      }
    };
    fetchBlogs();
  }, []);

  /* Derive popular tags from blogs */
  const popularTags = useMemo(() => getPopularTags(blogs, 10), [blogs]);

  /* Related posts — latest 3 by createdAt desc */
  const related = useMemo(() => {
    const sorted = [...blogs].sort((a, b) => {
      const da = new Date(a?.createdAt || 0).getTime();
      const db = new Date(b?.createdAt || 0).getTime();
      return db - da;
    });
    return sorted.slice(0, 3);
  }, [blogs]);

  const ClickHandler = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const SubmitHandler = (e) => {
    e.preventDefault();
    // TODO: implement search routing or client-side filtering here
  };

  return (
    <div className="col-lg-4">
      <aside className="sidebar ps-lg-5">
        {/* Search */}
        <div className="search_form">
          <h3 className="sidebar_widget_title">Search</h3>
          <form className="form-group" onSubmit={SubmitHandler}>
            <input
              className="form-control"
              type="search"
              name="search"
              placeholder="Search your keyword"
            />
            <button type="submit" aria-label="Search">
              <img src={icon} alt="Search Icon" />
            </button>
          </form>
        </div>

        {/* Related posts */}
        <div className="post_list_block">
          <h3 className="sidebar_widget_title">Related Posts</h3>

          {loadingBlogs && <p>Loading...</p>}
          {errorBlogs && <p className="text-danger">{errorBlogs}</p>}

          {!loadingBlogs && !errorBlogs && (
            <ul className="unordered_list_block">
              {related.map((blog) => (
                <li key={blog._id}>
                  <h3 className="post_title">
                    <Link onClick={ClickHandler} to={`/blog/${blog.blog_title.replace(/\s+/g, "-")}`}>
                      {blog.blog_title}
                    </Link>
                  </h3>

                  <ul className="post_meta unordered_list">
                    <li>
                      <Link onClick={ClickHandler} to={`/blog/${blog.blog_title.replace(/\s+/g, "-")}`}>
                        <img src={icon1} alt="Icon Calendar" /> {formatDMY(blog.createdAt)}
                      </Link>
                    </li>
                    <li>
                      <Link onClick={ClickHandler} to={`/blog/${blog.blog_title.replace(/\s+/g, "-")}`}>
                        <i className="fa-regular fa-tag" aria-hidden="true"></i>{" "}
                        {blog.category || "General"}
                      </Link>
                    </li>
                  </ul>
                </li>
              ))}

              {related.length === 0 && <li>No posts available.</li>}
            </ul>
          )}
        </div>

        {/* Popular tags */}
        <div className="popular_tags">
          <h3 className="sidebar_widget_title">Popular Tags</h3>
          <ul className="tags_list unordered_list">
            {popularTags.length > 0 ? (
              popularTags.map((tag) => (
                <li key={tag}>
                  <Link
                    onClick={ClickHandler}
                    to={`/blog`}
                  >
                    {tag}
                  </Link>
                </li>
              ))
            ) : (
              <li>
                <Link onClick={ClickHandler} to="/blog">
                  No tags
                </Link>
              </li>
            )}
          </ul>
        </div>
      </aside>
    </div>
  );
};

export default BlogSidebar;
